'use client';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect } from 'react';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { Button } from '@/app/components/ui/button';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { BorderedListSection } from '../../_components/BorderedListSection';
import ProductInputTool from '../../_components/ProductInputTool';
import { reportProductPageElementClick } from '../../_components/ProductInputTool/utils';

const ProductSteps = dynamic(() => import('@/app/components/shared/ProductSteps').then((mod) => mod.ProductSteps));
const QuestionSection = dynamic(() => import('./QuestionSection').then((mod) => mod.QuestionSection));
const RelatedProductsSection = dynamic(() =>
  import('../../_components/RelatedProductsSection').then((mod) => mod.RelatedProductsSection),
);

export const TextToVideo: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('ai-text-to-video');

  useEffect(() => {
    reportEvent('feature_page_view', {
      from_page: 'text to video',
    });
  }, []);

  // 处理生成按钮点击
  const handleGenerateClick = () => {
    reportEvent('element_click', {
      element_name: 'generate-now',
      page: 'ai-text-to-video',
    });
    reportProductPageElementClick('generate now');
    router.push('/create/text2video');
  };

  // 特性数据
  const features = [
    {
      id: 'feature-1',
      title: t('feature.lightningFast.title'),
      description: t('feature.lightningFast.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/features1-d57185e3.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/features1-d57185e3-f9c937e3.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/features1-3bdddb34.webp',
    },
    {
      id: 'feature-2',
      title: t('feature.2DAnimation.title'),
      description: t('feature.2DAnimation.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/features2-955dd56f-fa51d120.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/features2-955dd56f-128e612b.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/features2-8d1762ad.webp',
    },
    {
      id: 'feature-3',
      title: t('feature.RealisticAnimation.title'),
      description: t('feature.RealisticAnimation.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/features3-af70fde4-14306293.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/features3-af70fde4-cb371a1a.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/features3-3ca3f411.webp',
    },
    {
      id: 'feature-4',
      title: t('feature.Stunning.title'),
      description: t('feature.Stunning.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/features4-0cdbe4c9-80d6327f.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/features4-0cdbe4c9-df410ca8.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/features4-22fdb57c.webp',
    },
  ];

  const promptFormulaItems = (['subject', 'action', 'camera', 'scene', 'style'] as const).map((key) => ({
    id: key,
    title: t(`promptFormula.items.${key}.title`),
    description: t(`promptFormula.items.${key}.description`),
  }));

  // 步骤数据
  const stepItems = [
    {
      index: 1,
      title: t('step.step1.title'),
      description: t('step.step1.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step3-05215b70.webp',
      alt: 'How to use Vidu text to video — enter prompt',
    },
    {
      index: 2,
      title: t('step.step2.title'),
      description: t('step.step2.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step2-4dc82c79.webp',
      alt: 'How to use Vidu text to video — choose settings',
    },
    {
      index: 3,
      title: t('step.step3.title'),
      description: t('step.step3.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step-1d5bafb1.webp',
      alt: 'How to use Vidu text to video — generate and download',
    },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-black text-white">
      {/* Hero Section */}
      <section className="pt-[164px] sm:pt-16">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-full max-w-[50vw] text-center">
            <h1 className="mb-4 font-semibold text-5xl leading-tight sm:text-4xl">{t('title')}</h1>
            <p className="mb-12 font-normal text-xl sm:mb-6 sm:text-base">{t('subtitle')}</p>
          </div>
          <div className="mx-auto min-h-[227px] w-full max-w-[50vw] md:max-w-full">
            <ProductInputTool />
          </div>
          <div className="mt-12 w-[74vw] overflow-hidden rounded-xl">
            <VideoLazyLoad
              videoSrc="https://image01.cf.vidu.studio/vidu/media-asset/banner1-4735a925.mp4"
              mobileVideoSrc="https://image01.cf.vidu.studio/vidu/media-asset/banner1-4735a925-c9d4d72e.mp4"
              poster="https://image01.cf.vidu.studio/vidu/media-asset/banner1-c213f062.webp"
              priority={true}
              alt="Vidu AI video generator — text to cinematic video"
            />
          </div>
        </div>
      </section>

      {/* Features Sections */}
      {features.map((feature) => (
        <section key={feature.id} className="pt-[120px] sm:pt-10">
          <div className="mx-auto flex flex-col items-center px-4">
            <div className="w-[58vw] text-center">
              <h2 className="mb-4 font-semibold text-[40px] leading-tight sm:text-[36px]">{feature.title}</h2>
              <p className="font-normal text-base sm:text-sm">{feature.description}</p>
            </div>
            <div className="mt-16 w-[74vw] overflow-hidden rounded-xl">
              <VideoLazyLoad
                videoSrc={feature.videoSrc}
                mobileVideoSrc={feature.mobileVideoSrc}
                poster={feature.videoPoster}
                alt={`AI text to video example — ${feature.title}`}
              />
            </div>
          </div>
        </section>
      ))}

      {/* Prompt Formula */}
      <BorderedListSection
        title={t('promptFormula.title')}
        subtitle={t('promptFormula.subtitle')}
        items={promptFormulaItems}
      />

      {/* Steps Section */}
      <ProductSteps
        title={t('step.title')}
        items={stepItems}
        page="ai-text-to-video"
        theme="dark"
        redirectUrl="/create/text2video"
      />

      {/* Related Products Section */}
      <RelatedProductsSection currentSlug="ai-text-to-video" />

      {/* FAQ Section */}
      <QuestionSection />

      {/* CTA Banner */}
      <section className="relative mb-[120px] h-[433px] w-full sm:h-[219px]">
        {/* 视频背景 */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <VideoLazyLoad
            videoSrc="https://image01.cf.vidu.studio/vidu/media-asset/cta1-6a2f66c0-f50e62b9.mp4"
            mobileVideoSrc="https://image01.cf.vidu.studio/vidu/media-asset/cta1-6a2f66c0-94f0411f.mp4"
            poster="https://image01.cf.vidu.studio/vidu/media-asset/cta1-e38982a8.webp"
            className="h-full w-full object-cover"
            alt="AI text to video example — cinematic scene generated from prompt"
          />
        </div>

        {/* 内容覆盖层 */}
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-8">
            <h2 className="mb-8 font-bold text-[42px] sm:font-semibold sm:text-[32px]">{t('cta.title')}</h2>
            <Button
              className="h-[60px] min-w-[224px] rounded-[100px] bg-[#0086FF] px-10 py-5 font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-12 sm:w-[195px] sm:py-4 sm:text-base"
              onClick={handleGenerateClick}
            >
              {t('buttons.generateNow')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
